import React from 'react';

import jsonArraySort from '../../services/jsonArraySort';
import SortableHeader from '../../components/SortableHeader';
import I18n from '../../locales/i18n';

import './table.css';

function useSearchResults(results, filterFunction, serverSideSearch) {
	const [query, setQuery] = React.useState('');
	const [filteredResults, setFilteredResults] = React.useState(results);

	React.useMemo(() => {
		// If server-side search is enabled, skip client filtering
		if (serverSideSearch) {
			setFilteredResults(results);
			return;
		}
		const result = results.filter((user) => {
			return filterFunction(user)
				.toLowerCase()
				.includes(query.toLocaleLowerCase());
		});
		setFilteredResults(result);
	}, [results, query, filterFunction, serverSideSearch]);

	return { query, setQuery, filteredResults };
}

const EventLists = ({
	list = [],
	noDataMessage = I18n('TABLE', 'NO_DATA'),
	iterateFunction,
	filterFunction,
	fields = [],
	searchPlaceHolder = I18n('TABLE', 'SEARCH'),
	defaultSorter = { parameter: 'displayName', order: 'asc' },
	// Server-side pagination props
	serverSidePagination = false,
	pagination = null, // { page, limit, total, totalPages, hasNextPage, hasPrevPage }
	onPageChange = null,
	onLimitChange = null,
	onSearchChange = null,
	isLoading = false,
}) => {
	const { query, setQuery, filteredResults } = useSearchResults(
		list,
		filterFunction,
		serverSidePagination
	);
	const [sortParameter, setSortParameter] = React.useState(defaultSorter);

	// Client-side pagination state (only used when not server-side)
	const [maxRecordPerPage, setMaxRecordPerPage] = React.useState(25);
	const [currentPage, setCurrentPage] = React.useState(1);

	// Debounced search for server-side
	const searchTimeoutRef = React.useRef(null);

	React.useEffect(() => {
		if (!serverSidePagination) {
			setCurrentPage(1);
		}
	}, [maxRecordPerPage, serverSidePagination]);

	// Handle search with debounce for server-side
	const handleSearchChange = React.useCallback(
		(value) => {
			setQuery(value);
			if (serverSidePagination && onSearchChange) {
				if (searchTimeoutRef.current) {
					clearTimeout(searchTimeoutRef.current);
				}
				searchTimeoutRef.current = setTimeout(() => {
					onSearchChange(value);
				}, 400);
			}
		},
		[serverSidePagination, onSearchChange, setQuery]
	);

	// Cleanup timeout on unmount
	React.useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	// Handle limit change
	const handleLimitChange = React.useCallback(
		(value) => {
			if (serverSidePagination && onLimitChange) {
				onLimitChange(value === 'ALL' ? 1000 : parseInt(value, 10));
			} else {
				setMaxRecordPerPage(value);
			}
		},
		[serverSidePagination, onLimitChange]
	);

	// Show empty state
	if (filteredResults.length === 0 && !isLoading) {
		return (
			<React.Fragment>
				<div className="mg-t-20 mg-b-30">
					<div className="az-header-events-center">
						<input
							value={query}
							onChange={(e) => handleSearchChange(e.target.value)}
							type="search"
							className="form-control"
							placeholder={searchPlaceHolder}
							disabled={list.length === 0 && !serverSidePagination}
						/>
						<button className="btn">
							<i className="fas fa-search"></i>
						</button>
					</div>
				</div>
				<div className="table-no-data-messge">
					<h4>{noDataMessage}</h4>
				</div>
			</React.Fragment>
		);
	}

	// Client-side sorting (only if not server-side)
	let finalList = filteredResults;
	if (!serverSidePagination) {
		if (sortParameter.order === 'DESC') {
			filteredResults.sort(jsonArraySort(sortParameter.parameter).DESC);
		} else {
			filteredResults.sort(jsonArraySort(sortParameter.parameter).ASC);
		}
	}

	// Pagination data
	let paginateData = {
		startShow: 1,
		lastShow: 2,
		maxPage: 100,
		buttons: null,
	};

	if (serverSidePagination && pagination) {
		// Server-side pagination
		paginateData.startShow = (pagination.page - 1) * pagination.limit + 1;
		paginateData.lastShow = Math.min(
			pagination.page * pagination.limit,
			pagination.total
		);
		paginateData.maxPage = pagination.totalPages;

		const currentPageServer = pagination.page;

		// Generate pagination buttons for server-side
		if (paginateData.maxPage < 3) {
			const buttons = [];
			for (let i = 1; i <= paginateData.maxPage; i++) {
				buttons.push(
					<button
						key={i}
						disabled={currentPageServer === i}
						className={currentPageServer === i ? 'selected' : ''}
						onClick={() => onPageChange && onPageChange(i)}
					>
						{i}
					</button>
				);
			}
			paginateData.buttons = <React.Fragment>{buttons}</React.Fragment>;
		} else {
			if (currentPageServer === 1) {
				paginateData.buttons = (
					<React.Fragment>
						<button disabled={true} className={'selected'}>
							1
						</button>
						<button onClick={() => onPageChange && onPageChange(2)}>2</button>
						<button onClick={() => onPageChange && onPageChange(3)}>3</button>
					</React.Fragment>
				);
			} else if (currentPageServer === paginateData.maxPage) {
				paginateData.buttons = (
					<React.Fragment>
						<button
							onClick={() => onPageChange && onPageChange(currentPageServer - 2)}
						>
							{currentPageServer - 2}
						</button>
						<button
							onClick={() => onPageChange && onPageChange(currentPageServer - 1)}
						>
							{currentPageServer - 1}
						</button>
						<button disabled={true} className={'selected'}>
							{currentPageServer}
						</button>
					</React.Fragment>
				);
			} else {
				paginateData.buttons = (
					<React.Fragment>
						<button
							onClick={() => onPageChange && onPageChange(currentPageServer - 1)}
						>
							{currentPageServer - 1}
						</button>
						<button disabled={true} className={'selected'}>
							{currentPageServer}
						</button>
						<button
							onClick={() => onPageChange && onPageChange(currentPageServer + 1)}
						>
							{currentPageServer + 1}
						</button>
					</React.Fragment>
				);
			}
		}
	} else {
		// Client-side pagination
		if (maxRecordPerPage !== 'ALL') {
			const start = (currentPage - 1) * maxRecordPerPage;

			finalList = filteredResults.slice(start, start + maxRecordPerPage);

			paginateData.startShow = 1 + (currentPage - 1) * maxRecordPerPage;
			paginateData.lastShow =
				filteredResults.length < maxRecordPerPage
					? filteredResults.length
					: currentPage * maxRecordPerPage;
			paginateData.maxPage = Math.ceil(filteredResults.length / maxRecordPerPage);
		} else {
			paginateData.startShow = 1;
			paginateData.lastShow = filteredResults.length;
			paginateData.maxPage = 1;
		}

		// Generate pagination buttons for client-side
		if (paginateData.maxPage < 3) {
			paginateData.buttons = (
				<React.Fragment>
					<button
						disabled={currentPage === 1}
						className={currentPage === 1 ? 'selected' : ''}
						onClick={() => setCurrentPage(1)}
					>
						1
					</button>
					<button
						disabled={currentPage === 2}
						className={currentPage === 2 ? 'selected' : ''}
						onClick={() => setCurrentPage(2)}
					>
						2
					</button>
				</React.Fragment>
			);
		} else {
			if (currentPage === 1) {
				paginateData.buttons = (
					<React.Fragment>
						<button disabled={true} className={'selected'}>
							1
						</button>
						<button onClick={() => setCurrentPage(2)}>2</button>
						<button onClick={() => setCurrentPage(3)}>3</button>
					</React.Fragment>
				);
			} else if (currentPage === paginateData.maxPage) {
				paginateData.buttons = (
					<React.Fragment>
						<button onClick={() => setCurrentPage(currentPage - 2)}>
							{currentPage - 2}
						</button>
						<button onClick={() => setCurrentPage(currentPage - 1)}>
							{currentPage - 1}
						</button>
						<button disabled={true} className={'selected'}>
							{currentPage}
						</button>
					</React.Fragment>
				);
			} else {
				paginateData.buttons = (
					<React.Fragment>
						<button onClick={() => setCurrentPage(currentPage - 1)}>
							{currentPage - 1}
						</button>
						<button disabled={true} className={'selected'}>
							{currentPage}
						</button>
						<button onClick={() => setCurrentPage(currentPage + 1)}>
							{currentPage + 1}
						</button>
					</React.Fragment>
				);
			}
		}
	}

	// Current page and total for navigation
	const navCurrentPage = serverSidePagination ? pagination?.page || 1 : currentPage;
	const navMaxPage = paginateData.maxPage;
	const navLimit = serverSidePagination ? pagination?.limit || 25 : maxRecordPerPage;
	const navTotal = serverSidePagination ? pagination?.total || 0 : filteredResults.length;

	return (
		<React.Fragment>
			<div className="mg-t-20 mg-b-30 table-header">
				<div className="table-paginate-settings">
					<select
						value={navLimit}
						onChange={(e) => handleLimitChange(e.target.value)}
					>
						<option value={5}>5</option>
						<option value={10}>10</option>
						<option value={25}>25</option>
						<option value={50}>50</option>
						<option value={100}>100</option>
						<option value={200}>200</option>
						{!serverSidePagination && (
							<option value="ALL">{I18n('TABLE', 'ALL')}</option>
						)}
					</select>
					<span>{I18n('TABLE', 'RECORDS_PER_PAGE')}</span>
				</div>
				<div className="az-header-events-center">
					<input
						value={query}
						onChange={(e) => handleSearchChange(e.target.value)}
						type="search"
						className="form-control"
						placeholder={searchPlaceHolder}
						disabled={list.length === 0 && !serverSidePagination}
					/>
					<button className="btn">
						<i className="fas fa-search"></i>
					</button>
				</div>
			</div>
			<div className="table-responsive" style={{ position: 'relative' }}>
				{isLoading && (
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: 'rgba(255,255,255,0.7)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							zIndex: 10,
						}}
					>
						<i className="fas fa-spinner fa-spin fa-2x"></i>
					</div>
				)}
				<table className="table table-hover mg-b-0">
					<thead>
						<SortableHeader
							sortParameter={sortParameter}
							setSortParameter={setSortParameter}
							fields={fields}
						/>
					</thead>
					<tbody>
						{finalList.map(function (user, index) {
							return iterateFunction(user, index);
						})}
					</tbody>
				</table>
			</div>
			<div className="table-paginate">
				<div className="table-paginate-results">
					{`${I18n('TABLE', 'SHOWING')} `}
					{paginateData.startShow}
					{` ${I18n('COMMON', 'TO')} `}
					{Math.min(paginateData.lastShow, navTotal)}
					{` ${I18n('COMMON', 'OF')} `}
					{navTotal}
					{` ${I18n('COMMON', 'ROWS')} `}
					{!serverSidePagination &&
						query !== '' &&
						` (${I18n('TABLE', 'FILTERED_OF')} ${list.length} ${I18n(
							'TABLE',
							'RECORDS'
						)})`}
				</div>
				<div className="table-paginate-buttons">
					{navLimit !== 'ALL' && navLimit < navTotal && (
						<React.Fragment>
							<button
								onClick={() =>
									serverSidePagination
										? onPageChange && onPageChange(1)
										: setCurrentPage(1)
								}
							>
								<i className="fas fa-angle-double-left" />{' '}
							</button>
							<button
								disabled={navCurrentPage <= 1}
								onClick={() =>
									serverSidePagination
										? onPageChange && onPageChange(navCurrentPage - 1)
										: setCurrentPage(currentPage - 1)
								}
							>
								<i className="fas fa-angle-left" />{' '}
							</button>
							{paginateData.buttons}
							<button
								disabled={navCurrentPage >= navMaxPage}
								onClick={() =>
									serverSidePagination
										? onPageChange && onPageChange(navCurrentPage + 1)
										: setCurrentPage(currentPage + 1)
								}
							>
								<i className="fas fa-angle-right" />{' '}
							</button>
							<button
								onClick={() =>
									serverSidePagination
										? onPageChange && onPageChange(navMaxPage)
										: setCurrentPage(paginateData.maxPage)
								}
							>
								<i className="fas fa-angle-double-right" />{' '}
							</button>
						</React.Fragment>
					)}
				</div>
			</div>
		</React.Fragment>
	);
};

export default EventLists;
